/**
 * Generates HTML email template for "Itinerary Proposal for Review".
 * Sent to clients with trip quote details, leg info, passengers, crew, services, and aircraft options.
 * Uses shared layout from emailLayout.js for header/footer.
 */

import moment from 'moment-timezone';
import { getAirportTimezone, formatDateInAirportTimezone, formatTimeInAirportTimezone } from '@/utils/airportTimezone';
import { apiClient as api } from '@/lib/api';
import { getDistance } from 'geolib';
import { icon, spacer, emailDocOpen, emailDocClose, emailHeader, emailFooter } from '../emailLayout';
import { getAircraftPhotos } from '@/features/tails/utils';
import { formatPrice, trunc2 } from '@/utils';

// Plane icon rotated 90° clockwise (pointing right) for the leg route line
const planeRight = (size = 16) => {
  const baseUrl = process.env.REACT_APP_ENVIRONMENT=="LOCAL" ? window.location.origin : (process.env.REACT_APP_ENVIRONMENT=="PROD" ? "https://app.trustjet.ai" : "https://dev.trustjet.ai");
  return `<img src="${baseUrl}/images/icons/planeright.svg" width="${size}" height="${size}" alt="" style="display:inline-block;vertical-align:middle;" />`;
};

const getModelName = (aircraft) => {
  if (!aircraft) return '';
  if (aircraft.aircraft_make_model) {
    const { manufacturer } = aircraft.aircraft_make_model;
    const makeModel = aircraft.make_model || aircraft.aircraft_make_model.make_model || '';
    if (manufacturer && makeModel) {
      const modelPart = makeModel.replace(new RegExp(`^${manufacturer}[- ]*`, 'i'), '');
      return `${manufacturer} ${modelPart}`.trim();
    }
    return makeModel || '';
  }
  return aircraft.make_model || aircraft.tail_number || '';
};

/**
 * Enrich legs with arrival times, distance, and timezone (same as TripSheet)
 */
const enrichLegs = async (legs) => {
  await Promise.all(legs.map(async (leg) => {
    try {
      // Fetch airport data for coordinates and timezone
      const [dep, arr] = await Promise.all([
        api.getAirportByIcao(leg.departure_airport),
        api.getAirportByIcao(leg.arrival_airport),
      ]);

      // Compute distance using geolib
      if (dep?.latitude && dep?.longitude && arr?.latitude && arr?.longitude) {
        const distanceKm = getDistance(
          { latitude: dep.latitude, longitude: dep.longitude },
          { latitude: arr.latitude, longitude: arr.longitude },
        ) / 1000;
        leg.distance_nm = Math.round(distanceKm * 0.539957);
      }

      // Store timezone from airport data
      if (dep?.time_zone) {
        leg.dep_timezone = dep.time_zone;
      }

      // Fetch arrival time from API
      if (leg.departure_date) {
        const depTime = formatTimeInAirportTimezone(leg.departure_date, leg.departure_airport, { hour12: true, timeZoneName: 'short' });
        const arrivalResult = await api.getArrivalTime({
          from_location: leg.departure_airport,
          to_location: leg.arrival_airport,
          departure_time: depTime,
        });

        if (arrivalResult?.arrival_time) {
          const raw = arrivalResult.arrival_time;
          const match = raw.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)/i);
          if (match) {
            let h = parseInt(match[1], 10);
            const m = match[2];
            const period = match[3].toUpperCase();
            if (period === 'PM' && h !== 12) h += 12;
            if (period === 'AM' && h === 12) h = 0;
            const arrivalTime24h = `${String(h).padStart(2, '0')}:${m}`;

            // Compute arrival date (may cross midnight)
            const arrTz = getAirportTimezone(leg.arrival_airport);
            const depInArrTz = moment.utc(leg.departure_date).tz(arrTz);
            const arrDateStr = depInArrTz.format('YYYY-MM-DD');
            const arrMoment = moment.tz(`${arrDateStr} ${arrivalTime24h}`, 'YYYY-MM-DD HH:mm', arrTz);
            if (arrMoment.valueOf() < moment.utc(leg.departure_date).valueOf()) {
              arrMoment.add(1, 'day');
            }
            leg.arrival_date = arrMoment.toISOString();

            // Compute flight duration
            const durationMin = Math.round((arrMoment.valueOf() - moment.utc(leg.departure_date).valueOf()) / 60000);
            if (durationMin > 0) {
              const hrs = Math.floor(durationMin / 60);
              const mins = durationMin % 60;
              leg.flight_time = `${hrs}h ${String(mins).padStart(2, '0')}m`;
            }
          }
        }
      }
    } catch (e) {
      // Silently skip if any lookup fails
    }
  }));
};

/**
 * Build a single leg detail block
 */
const buildLegHtml = (leg, index) => {
  const depDate = leg.departure_date || null;
  const arrDate = leg.arrival_date || leg.arrival_time || null;

  const depTime = depDate ? formatTimeInAirportTimezone(depDate, leg.departure_airport) : '';
  const arrTime = arrDate ? formatTimeInAirportTimezone(arrDate, leg.arrival_airport) : '';
  const depDateStr = depDate ? formatDateInAirportTimezone(depDate, leg.departure_airport, { year: 'numeric' }) : '';
  const arrDateStr = (arrDate || depDate) ? formatDateInAirportTimezone(arrDate || depDate, leg.arrival_airport, { year: 'numeric' }) : '';
  const depDay = depDate ? formatDateInAirportTimezone(depDate, leg.departure_airport, { weekday: 'long', day: undefined, month: undefined }) : '';
  const arrDay = (arrDate || depDate) ? formatDateInAirportTimezone(arrDate || depDate, leg.arrival_airport, { weekday: 'long', day: undefined, month: undefined }) : '';
  const depTzLabel = depDate ? formatTimeInAirportTimezone(depDate, leg.departure_airport, { timeZoneName: 'short' }).replace(/^\d{1,2}:\d{2}\s*/, '') : '';
  const arrTzLabel = (arrDate || depDate) ? formatTimeInAirportTimezone(arrDate || depDate, leg.arrival_airport, { timeZoneName: 'short' }).replace(/^\d{1,2}:\d{2}\s*/, '') : '';

  const etaDisplay = leg.flight_time || leg.eta || '';
  const distanceDisplay = leg.distance_nm ? `${leg.distance_nm} NM` : (leg.distance ? `${leg.distance} NM` : '');

  return `
  ${spacer()}
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <tr>
            <td style="background: #f7f7f7;padding: 10px 30px;">
              <span style="font-size:11px;text-transform:uppercase;color:#9CA3AF;letter-spacing:0.5px;font-weight:600;">LEG ${index + 1}</span>
            </td>
          </tr>
          <!-- Timezone / Distance labels -->
          <tr>
            <td style="padding:12px 32px 0;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td valign="bottom">
                    <p style="margin:0;font-size:11px;text-transform:uppercase;color:#9CA3AF;letter-spacing:0.3px;">${depTzLabel || 'UTC-05'}</p>
                  </td>
                  <td valign="bottom" align="right">
                    <p style="margin:0;font-size:11px;text-transform:uppercase;color:#9CA3AF;letter-spacing:0.3px;">${distanceDisplay}</p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <!-- Airport codes with connecting line -->
          <tr>
            <td style="padding:2px 32px 0;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td width="25%" valign="middle">
                    <p style="margin:0;font-size:42px;font-weight:700;color:#111827;letter-spacing:-1px;">${leg.departure_airport || ''}</p>
                  </td>
                  <td valign="middle" align="center" style="padding:0;">
                    <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                      <tr>
                        <td style="width:40%;vertical-align:middle;"><div style="height:1px;background-color:#C0C0C0;"></div></td>
                        <td align="center" style="padding:0 10px;vertical-align:middle;">${planeRight(20, '#374151')}</td>
                        <td style="width:40%;vertical-align:middle;"><div style="height:1px;background-color:#C0C0C0;"></div></td>
                      </tr>
                    </table>
                  </td>
                  <td width="25%" valign="middle" align="right">
                    <p style="margin:0;font-size:42px;font-weight:700;color:#111827;letter-spacing:-1px;">${leg.arrival_airport || ''}</p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <!-- Times, dates, duration -->
          <tr>
            <td style="padding:8px 32px 20px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td width="35%" valign="top">
                    <p style="margin:0 0 2px;font-size:13px;font-weight:600;color:#374151;">${depTime}${depTzLabel ? ` ${depTzLabel}` : ''}</p>
                    <p style="margin:0 0 2px;font-size:13px;color:#374151;">${depDateStr}</p>
                    <p style="margin:0;font-size:12px;color:#9CA3AF;">${depDay}</p>
                  </td>
                  <td width="30%" valign="top" align="center">
                    ${etaDisplay ? `<p style="margin:0;font-size:13px;color:#9CA3AF;">${etaDisplay}</p>` : ''}
                  </td>
                  <td width="35%" valign="top" align="right">
                    <p style="margin:0 0 2px;font-size:13px;font-weight:600;color:#374151;">${arrTime}${arrTzLabel ? ` ${arrTzLabel}` : ''}</p>
                    <p style="margin:0 0 2px;font-size:13px;color:#374151;">${arrDateStr}</p>
                    <p style="margin:0;font-size:12px;color:#9CA3AF;">${arrDay}</p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
</table>   
        `;

};

/**
 * Build passenger list HTML
 */
const buildPassengersHtml = (passengers) => {
  if (!passengers || passengers.length === 0) return '';

  const cardStyle = 'border-radius:10px;padding:12px 16px;background-color: #f7f7f7;';

  const passengerCells = passengers.map(p => {
    const name = typeof p === 'string' ? p : [p.first_name, p.last_name].filter(Boolean).join(' ');
    return `<td width="50%" style="padding:0 4px 8px 0;vertical-align:top;">
                      <div style="${cardStyle}">
                        <span style="font-size:14px;font-weight:500;color:#111827;">${name}</span>
                      </div>
                    </td>`;
  });

  // Lay out passengers in 2-column rows
  const rows = [];
  for (let i = 0; i < passengerCells.length; i += 2) {
    const cell1 = passengerCells[i];
    const cell2 = passengerCells[i + 1] || '<td width="50%"></td>';
    rows.push(`<tr>${cell1}${cell2}</tr>`);
  }

  return `
          <tr>
            <td style="padding:20px 32px 12px;">
              <p style="margin:0 0 12px;font-size:11px;text-transform:uppercase;color:#9CA3AF;letter-spacing:0.5px;font-weight:600;">Passengers</p>
              <table role="presentation" cellpadding="0" cellspacing="0" width="100%">
                ${rows.join('')}
              </table>
            </td>
          </tr>`;
};

/**
 * Build crew section HTML
 */
const buildCrewHtml = (crew) => {
  if (!crew) return '';

  const crewMembers = [];
  if (crew.pic) {
    crewMembers.push({
      role: 'PIC',
      name: typeof crew.pic === 'string' ? crew.pic : [crew.pic.first_name, crew.pic.last_name].filter(Boolean).join(' '),
      phone: crew.pic.phone || crew.pic.phone_number || '',
    });
  }
  if (crew.sic) {
    crewMembers.push({
      role: 'SIC',
      name: typeof crew.sic === 'string' ? crew.sic : [crew.sic.first_name, crew.sic.last_name].filter(Boolean).join(' '),
      phone: crew.sic.phone || crew.sic.phone_number || '',
    });
  }

  if (crewMembers.length === 0) return '';

  const cardStyle = 'border-radius:10px;padding:12px 16px;background-color: #f7f7f7;';

  const crewCells = crewMembers.map(c => `
                    <td width="50%" style="padding:0 4px 8px 0;vertical-align:top;">
                      <div style="${cardStyle}">
                        <p style="margin:0 0 4px;font-size:14px;font-weight:600;color:#111827;">${c.role}: ${c.name}</p>
                        ${c.phone ? `<p style="margin:0;font-size:13px;color:#9CA3AF;">${c.phone}</p>` : ''}
                      </div>
                    </td>`).join('');

  return `
          <tr>
            <td style="padding:4px 32px 12px;">
              <p style="margin:0 0 12px;font-size:11px;text-transform:uppercase;color:#9CA3AF;letter-spacing:0.5px;font-weight:600;">Crew</p>
              <table role="presentation" cellpadding="0" cellspacing="0" width="100%">
                <tr>
                  ${crewCells}
                </tr>
              </table>
            </td>
          </tr>`;
};

/**
 * Build additional services section HTML
 */
const buildServicesHtml = (services) => {
  if (!services || services.length === 0) return '';

  const thStyle = 'padding:10px 0;font-size:11px;text-transform:uppercase;color:#374151;letter-spacing:0.3px;font-weight:600;';
  const tdStyle = 'padding:10px 0;font-size:13px;color:#374151;';

  // Top section: Name | Service Details | Address
  const detailRows = services.map(s => `
                <tr>
                  <td width="35%" style="${tdStyle}">
                    ${s.name || ''}
                  </td>
                  <td width="35%" style="${tdStyle}">${s.details || '-'}</td>
                  <td width="30%" style="${tdStyle}">${s.address || '-'}</td>
                </tr>`).join('');

  // Bottom section: Phone Number | Confirmation Number
  const hasPhoneData = services.some(s => s.phone || s.confirmation_number);
  const phoneRows = hasPhoneData ? services.map(s => `
                <tr>
                  <td width="50%" style="${tdStyle}">${s.phone || '-'}</td>
                  <td width="50%" style="${tdStyle}">${s.confirmation_number || '-'}</td>
                </tr>`).join('') : '';

  return `
        ${spacer()}

        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <!-- Header bar -->
          <tr>
            <td style="padding:20px 32px 12px;">
              <p style="margin:0 0 12px;font-size:11px;text-transform:uppercase;color:#9CA3AF;letter-spacing:0.5px;font-weight:600;">Additional Services</p>
            </td>
          </tr>
          <!-- Name / Service Details / Address -->
          <tr>
            <td style="padding:0px 32px 0;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td width="35%" style="${thStyle}">Name</td>
                  <td width="35%" style="${thStyle}">Service Details</td>
                  <td width="30%" style="${thStyle}">Address</td>
                </tr>
                ${detailRows}
              </table>
            </td>
          </tr>
          ${hasPhoneData ? `
          <!-- Phone / Confirmation -->
          <tr>
            <td style="padding:8px 32px 20px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td width="50%" style="${thStyle}border-bottom:none;">Phone Number</td>
                  <td width="50%" style="${thStyle}border-bottom:none;">Confirmation Number</td>
                </tr>
                ${phoneRows}
              </table>
            </td>
          </tr>` : ''}
        </table>`;
};

/**
 * Build a single aircraft card HTML block
 */
const buildAircraftCardHtml = (option, quoteLink) => {
  const aircraft = option?.aircraft;
  const operator = option?.operator;
  const imageUrl = getAircraftPhotos(aircraft);
  const modelName = getModelName(aircraft);
  const operatorName = operator?.name || '';
  const operatorLocation = [operator?.city, operator?.state].filter(Boolean).join(', ');
  const operatorDisplay = operatorLocation ? `${operatorName} \u2022 ${operatorLocation}` : operatorName;
  const price = formatPrice(trunc2(option?.total_price || option?.final_price));

  const badges = [];
  if (operator?.argus_rating && operator.argus_rating !== 'No') {
    badges.push({ label: operator.argus_rating.toUpperCase(), bg: '#e8e9eb', color: '#374151', iconName: 'trophy' });
  }
  if (operator?.wyvern_rating && operator.wyvern_rating !== 'No') {
    badges.push({ label: 'WINGMAN', bg: '#e8e9eb', color: '#374151', iconName: 'medal' });
  }
  if (aircraft?.aircraft_features?.no_aog) {
    badges.push({ label: 'NO AOG', bg: '#DCFCE7', color: '#15803D', iconName: 'check' });
  }

  const badgesHtml = badges.map(b =>
    `<span style="display:inline-block;padding:4px 10px;background:${b.bg};color:${b.color};font-size:11px;font-weight:600;border-radius:16px;margin-right:6px;margin-bottom:4px;">${icon(b.iconName, 14, b.color)} ${b.label}</span>`
  ).join('');

  const amenities = aircraft?.aircraft_features?.custom_features || [];
  const amenitiesHtml = amenities.length > 0
    ? amenities.map(a =>
        `<span style="display:inline-block;padding:4px 12px;background:#e8e9eb;color:#374151;font-size:12px;font-weight:500;border-radius:16px;margin-right:6px;margin-bottom:6px;">${icon('check', 14, '#374151')} ${a}</span>`
      ).join('')
    : '';

  return `
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          ${imageUrl ? `
          <tr>
            <td style="padding:0;line-height:0;font-size:0;">
              <img src="${imageUrl}" alt="${modelName}" width="600" style="display:block;width:100%;height:auto;object-fit:cover;max-height:280px;" />
            </td>
          </tr>` : ''}
          <tr>
            <td style="padding:20px 32px;background-color:#FFFFFF;">
              <h2 style="margin:0 0 4px;font-size:20px;font-weight:500;color:#171717;">${aircraft?.tail_number || ''}</h2>
              <p style="margin:0 0 2px;font-size:13px;color:#6B7280;">${icon('plane', 14, '#6B7280')} ${modelName}</p>
              ${operatorDisplay ? `<p style="margin:0 0 12px;font-size:13px;color:#6B7280;">${icon('building', 14, '#6B7280')} ${operatorDisplay}</p>` : ''}

              <!-- Price -->
              <div style="display:inline-block;padding:6px 14px;background-color:#E8F4FD;border-radius:16px;margin-bottom:12px;">
                <span style="font-size:14px;font-weight:700;color:#0C3E71;">${icon('priceTag', 14, '#0C3E71')} ${price}</span>
              </div>

              <!-- Badges -->
              ${badgesHtml ? `<div style="margin-top:8px;">${badgesHtml}</div>` : ''}

              <!-- About -->
              ${aircraft?.description ? `
              <div style="margin-top:16px;">
                <h3 style="margin:0 0 6px;font-size:13px;font-weight:600;color:#6B7280;">About the Aircraft</h3>
                <p style="margin:0;font-size:13px;color:#171717;line-height:1.5;">${aircraft.description}</p>
              </div>` : ''}

              <!-- Amenities -->
              ${amenitiesHtml ? `
              <div style="margin-top:16px;">
                <h3 style="margin:0 0 8px;font-size:13px;font-weight:600;color:#6B7280;">Amenities</h3>
                <div>${amenitiesHtml}</div>
              </div>` : ''}

              ${quoteLink ? `
              <div style="margin-top:20px;">
                <a href="${quoteLink}&option=${option.bid_option_id}" target="_blank" style="display:block;padding:14px 0;background-color:#E8EDF4;color:#2C3E6B;font-size:14px;font-weight:600;text-decoration:none;border-radius:24px;text-align:center;">${icon('plane', 16, '#2C3E6B')} Book This Flight</a>
              </div>` : ''}
            </td>
          </tr>
        </table>`;
};

/**
 * Build the itinerary proposal email HTML
 *
 * @param {Object} params
 * @param {Object} params.quote - Full quote object
 * @param {string} [params.quoteLink] - Link to the quote preview page
 * @param {Array} [params.passengers] - Array of passenger objects ({ first_name, last_name }) or name strings
 * @param {Object} [params.crew] - Crew data { pic: { first_name, last_name, phone }, sic: { ... } }
 * @param {Array} [params.services] - Additional services [{ name, icon, details, address, phone, confirmation_number }]
 * @returns {Promise<string>} HTML email body
 */
export const buildItineraryProposalEmailHtml = async ({
  quote,
  quoteLink,
  passengers,
  crew,
  services,
  organization,
}) => {
  const client = quote?.client;
  const clientName = [client?.first_name, client?.last_name].filter(Boolean).join(' ') || 'Valued Client';
  const organizationName = quote?.bid_items?.[0]?.bid?.broker_org?.name || '';

  // Get legs from bid items
  const legs = quote?.bid_items?.flatMap(item => item.bid?.leg_itinerary || []) || [];

  // Enrich legs with arrival times, distance, timezone (same as TripSheet)
  await enrichLegs(legs);

  // Get all bid options
  const bidOptions = quote?.bid_items
    ?.flatMap(bi => bi.bid?.bid_options || []) || [];

  const optionCount = bidOptions.length;

  // Quote validity
  const expiryRaw = quote?.bid_items?.[0]?.bid?.valid_until;
  const validUntil = expiryRaw ? new Date(expiryRaw).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  }) : '';

  // Build leg sections
  const legsHtml = legs.map((leg, i) => buildLegHtml(leg, i)).join('');

  // Build aircraft cards
  const aircraftCardsHtml = bidOptions
    .map(option => buildAircraftCardHtml(option, quoteLink))
    .join(`\n        ${spacer()}\n`);

  return `
${emailDocOpen({ title: 'Itinerary Proposal for Review' })}
${emailHeader({ title: 'Itinerary Proposal for Review', organization })}

        ${spacer()}

        <!-- ===== CLIENT / QUOTE REF row ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <tr>
            <td style="padding:16px 32px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td>
                    <span style="font-size:11px;text-transform:uppercase;color:#9CA3AF;letter-spacing:0.5px;font-weight:600;">Trip Quotation For</span><br/>
                    <span style="font-size:16px;font-weight:600;color:#111827;">${clientName}</span>
                  </td>
                  <td align="right">
                    <span style="font-size:11px;text-transform:uppercase;color:#9CA3AF;letter-spacing:0.5px;font-weight:600;">Company</span><br/>
                    <span style="font-size:16px;font-weight:600;color:#111827;">${organizationName}</span>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>

        ${spacer()}

        <!-- ===== INTRO MESSAGE ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <tr>
            <td style="padding:28px 32px 24px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td width="56" valign="top" style="padding-right:16px;">
                    <div style="width:48px;height:48px;background-color:#DCFCE7;border-radius:50%;text-align:center;line-height:48px;">
                      ${icon('check', 24, '#16A34A')}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td valign="middle" style="padding-top:12px;">
                    <p style="margin:0;font-size:14px;color:#374151;line-height:1.6;">
                      You just received a trip quote consisting of ${optionCount} aircraft option${optionCount !== 1 ? 's' : ''}. Kindly review and select an aircraft below.
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>

        <!-- ===== LEG DETAILS ===== -->
        ${legsHtml}

        ${spacer()}

        ${(passengers?.length > 0 || crew) ? `
        <!-- ===== PASSENGERS + CREW ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          ${buildPassengersHtml(passengers)}
          ${buildCrewHtml(crew)}

          ${validUntil ? `
          <tr>
            <td style="padding:12px 32px 20px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td style="background-color:#e7e7e7;border-radius:20px;padding:3px 0;text-align:center;">
                    <span style="font-size:12px;color:#374151;">${icon('calendar', 14, '#9CA3AF')} Expires on ${validUntil}</span>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        ` : ''}

        </table>
        ` : ''}

        ${buildServicesHtml(services)}

        ${spacer()}

        <!-- ===== AIRCRAFT CARDS ===== -->
        ${aircraftCardsHtml}

        ${spacer()}

        ${validUntil ? `

        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#efefef;overflow:hidden;">
          <tr>
            <td style="padding:16px 32px;">
                <p style="margin:0;font-size:12px;line-height:1.5;color: #4c4c4c;">
                  This quote is valid until <strong>${validUntil}</strong>. Prices are subject to availability and may change.
                </p>
            </td>
          </tr>
        </table>` : ''}

${emailFooter({ organization })}
${emailDocClose()}`;
};
